import { useState, useRef, useEffect, useCallback } from 'react';
import { pdfjs } from 'react-pdf';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

// Configure worker (same as react-pdf uses)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function PdfViewer({ url, initialPage = 1, scale = 1, onLoadSuccess: onLoadSuccessProp, className = '' }) {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(initialPage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const pdfDocRef = useRef(null);
  const renderTaskRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Load the PDF document
  useEffect(() => {
    if (!url) return;

    let destroyed = false;
    let loadingTask = null;

    const loadPdf = async () => {
      setLoading(true);
      setError(null);
      setNumPages(0);
      setPageNumber(initialPage);

      // Cancel any in-flight render from previous document
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch {}
        renderTaskRef.current = null;
      }
      // Destroy previous document
      if (pdfDocRef.current) {
        try { pdfDocRef.current.destroy(); } catch {}
        pdfDocRef.current = null;
      }

      try {
        // Fetch PDF as ArrayBuffer ourselves to avoid pdfjs stream issues
        const response = await fetch(url, { credentials: 'include' });
        if (!response.ok) throw new Error(`Failed to load PDF (${response.status})`);
        if (destroyed) return;

        const arrayBuffer = await response.arrayBuffer();
        if (destroyed) return;

        // Load with pdfjs using the ArrayBuffer (no network stream for pdfjs to manage)
        loadingTask = pdfjs.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;

        if (destroyed) {
          pdf.destroy();
          return;
        }

        pdfDocRef.current = pdf;
        const np = pdf.numPages;
        setNumPages(np);
        setLoading(false);
        if (onLoadSuccessProp) onLoadSuccessProp({ numPages: np });
      } catch (err) {
        if (destroyed) return;
        if (err.name === 'AbortError' || err.message?.includes('destroy')) return;
        setLoading(false);
        setError(err.message || 'Failed to load PDF');
      }
    };

    loadPdf();

    return () => {
      destroyed = true;
      if (loadingTask) {
        try { loadingTask.destroy(); } catch {}
      }
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch {}
        renderTaskRef.current = null;
      }
      if (pdfDocRef.current) {
        try { pdfDocRef.current.destroy(); } catch {}
        pdfDocRef.current = null;
      }
    };
  }, [url, initialPage, onLoadSuccessProp]);

  // Render the current page to the canvas
  useEffect(() => {
    const pdf = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas || numPages === 0) return;

    let cancelled = false;

    const renderPage = async () => {
      // Cancel previous render
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch {}
        renderTaskRef.current = null;
      }

      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;

        const viewport = page.getViewport({ scale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = '100%';
        canvas.style.height = 'auto';

        const ctx = canvas.getContext('2d');
        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;

        await task.promise;
        renderTaskRef.current = null;
      } catch (err) {
        // Silently ignore all render cancellation / worker destruction errors
        if (cancelled) return;
        const msg = String(err?.message || err || '');
        if (/cancel|abort|destroy|sendWithPromise|transport|BodyStream/i.test(msg)) return;
        if (mountedRef.current) {
          console.warn('PDF render error:', msg);
        }
      }
    };

    renderPage();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch {}
        renderTaskRef.current = null;
      }
    };
  }, [pageNumber, numPages, scale]);

  if (!url) return null;

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid="pdf-viewer">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#F7FAFB]/80">
          <div className="flex items-center gap-2 text-[#1D4053] text-sm">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading PDF...
          </div>
        </div>
      )}
      {error && (
        <div className="flex items-center justify-center h-full text-red-500 text-sm p-4">
          {error}
        </div>
      )}
      <canvas ref={canvasRef} className="block" data-testid="pdf-canvas" />
      {numPages > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm border border-[#E2E8ED] rounded-full px-2 py-1 shadow-sm z-20" data-testid="pdf-page-nav">
          <button
            disabled={pageNumber <= 1}
            onClick={(e) => { e.stopPropagation(); setPageNumber(p => Math.max(1, p - 1)); }}
            className="p-1 rounded-full hover:bg-[#72A4BF]/10 disabled:opacity-30"
            data-testid="pdf-prev-page"
          >
            <ChevronLeft className="w-3.5 h-3.5 text-[#0D1830]" />
          </button>
          <span className="text-xs font-medium text-[#0D1830] min-w-[60px] text-center">
            {pageNumber} / {numPages}
          </span>
          <button
            disabled={pageNumber >= numPages}
            onClick={(e) => { e.stopPropagation(); setPageNumber(p => Math.min(numPages, p + 1)); }}
            className="p-1 rounded-full hover:bg-[#72A4BF]/10 disabled:opacity-30"
            data-testid="pdf-next-page"
          >
            <ChevronRight className="w-3.5 h-3.5 text-[#0D1830]" />
          </button>
        </div>
      )}
    </div>
  );
}
